import { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useCountdown, useLive } from '../lib/useLive'
import { ResultsView } from '../components/Results'
import { Counter, Leaders, LiveDot, Offline, Podium, Timer } from '../components/Bits'
import { useSeo } from '../lib/seo'

/**
 * Экран для проектора: только результат, крупно и без управления.
 * Отдельная страница нужна именно потому, что админку показывать нельзя —
 * там видны правильные ответы до их раскрытия.
 */
export default function Present() {
  const code = (useParams().code ?? '').toUpperCase()
  const [title, setTitle] = useState('')
  const stageRef = useRef<HTMLDivElement>(null)

  useSeo({ title: `${title || 'Эфир'} · экран — clapme`, noindex: true })

  const live = useLive({ role: 'present', code })
  const { state, results, leaderboard, reconnecting, onTaps } = live
  const slide = state?.slide ?? null
  const msLeft = useCountdown(state?.msLeft)

  const joinHref = `${location.protocol}//${location.host}/join/${code}`

  useEffect(() => {
    api.info(code).then((info) => setTitle(info.title)).catch(() => setTitle(''))
  }, [code])

  useGlow(onTaps, stageRef)

  return (
    <div className="stage" ref={stageRef}>
      <Offline reconnecting={reconnecting} />

      <header className="row spread row-wrap" style={{ gap: 20 }}>
        <div className="col" style={{ gap: 6 }}>
          {state?.ended ? <span className="eyebrow">эфир завершён</span> : <LiveDot />}
          {/* Счётчик «слайд 1 из 1» не показываем: статус эфира уже говорит
              лейбл «В эфире», а на однослайдовых эфирах счётчик и вовсе ни о чём. */}
          <h2 className="stage-head-title">{title || 'clapme'}</h2>
        </div>

        <div className="row" style={{ gap: 28 }}>
          <div className="col center" style={{ gap: 4 }}>
            <span className="eyebrow">в зале</span>
            <Counter value={state?.participants ?? 0} className="stat stat-lg" />
          </div>
          {/* «Ответили: 0» на неоткрытом слайде — лишний шум на экране. */}
          {results && results.view !== 'EMOJI_FLOW' && slide?.phase !== 'IDLE' && !state?.ended && (
            <div className="col center" style={{ gap: 4 }} data-testid="answered">
              <span className="eyebrow">ответили</span>
              <span className="stat stat-lg">
                <Counter value={results.total} />
              </span>
            </div>
          )}
          {!state?.ended && slide?.phase === 'OPEN' && (
            <Timer msLeft={msLeft} totalSeconds={slide.timerSeconds} />
          )}
        </div>
      </header>

      {state?.ended ? (
        <div className="stage-body center col" style={{ gap: 36 }}>
          <h1 className="stage-title">Спасибо!</h1>
          {leaderboard.length > 0 ? (
            <Podium rows={leaderboard} />
          ) : (
            <p className="stage-hint">Эфир завершён</p>
          )}
        </div>
      ) : slide ? (
        <div className="stage-body">
          <h1 className="stage-title">{slide.title}</h1>
          {slide.phase === 'IDLE' ? (
            <p className="stage-hint">Голосование ещё не открыто</p>
          ) : (
            <ResultsView results={results} subscribe={onTaps} scale={2} />
          )}
          {/* Подиум — только финал эфира. На отдельном слайде он и место занимает,
              и лишает концовку эффекта, поэтому здесь компактная тройка. */}
          {results?.revealed && leaderboard.length > 0 && (
            <Leaders rows={leaderboard.slice(0, 3)} title="Пока впереди" />
          )}
        </div>
      ) : !state ? (
        /* Состояние эфира ещё не приехало. Пустая сцена на долю секунды лучше,
           чем мелькнувший экран входа поверх уже идущего вопроса. */
        <div className="stage-body" />
      ) : (
        /* Пока вопроса нет, вход и есть содержание экрана: QR и код во всю высоту.
           Код крупнее всего — опоздавшие набирают его руками, стоя в проходе. */
        <div className="join-card join-card-hero">
          <div className="qr">
            <QRCodeSVG value={joinHref} size={512} level="M" />
          </div>
          <div className="col" style={{ gap: 'clamp(12px, 2.4vh, 28px)' }}>
            <span className="eyebrow">{location.host} · код эфира</span>
            <div className="waiting-code">{code}</div>
            <div className="join-steps">
              <span>
                <strong>Отсканируйте QR-код</strong>
              </span>
              <span>и отвечайте со своего телефона</span>
            </div>
          </div>
        </div>
      )}

      {/* Плашка входа висит весь эфир — в зал всё время подходят опоздавшие, —
          но на завершённом эфире входить уже некуда, и она только мешает финалу.
          А на экране ожидания вход и так занимает весь экран (см. выше). */}
      {!state?.ended && slide && (
      <footer className="join-card">
        <div className="qr">
          <QRCodeSVG value={joinHref} size={256} level="M" />
        </div>
        {/* Ссылку у QR не пишем: с последнего ряда её всё равно не прочитать.
            Призыв слева и домен с кодом справа закрывают оба пути входа —
            скан камерой и набор руками. */}
        <span className="join-cta">Отсканируйте, чтобы ответить</span>
        <div className="grow" />
        <div className="col center" style={{ gap: 4 }}>
          <span className="eyebrow">{location.host} · код эфира</span>
          <span className="big-code">{code}</span>
        </div>
      </footer>
      )}
    </div>
  )
}

/**
 * Привязывает свечение экрана к потоку реакций: каждая дельта тапов добавляет
 * яркости, а между дельтами она затухает.
 *
 * Значение пишется прямо в CSS-переменную, без useState: обновления идут
 * несколько раз в секунду, и перерисовывать из-за подсветки всё дерево незачем.
 */
function useGlow(
  onTaps: (listener: (deltas: Record<string, number>) => void) => () => void,
  ref: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    let level = 0

    const unsubscribe = onTaps((deltas) => {
      const taps = Object.values(deltas).reduce((sum, n) => sum + n, 0)
      // Насыщение: после десятка тапов за пачку ярче уже не становится,
      // иначе овации выжигают экран в белое.
      level = Math.min(1, level + Math.min(taps, 12) / 14)
    })

    const decay = window.setInterval(() => {
      level = Math.max(0, level - 0.12)
      ref.current?.style.setProperty('--glow', level.toFixed(2))
    }, 220)

    return () => {
      unsubscribe()
      window.clearInterval(decay)
      ref.current?.style.removeProperty('--glow')
    }
  }, [onTaps, ref])
}
